import type {
  ExtractedDrawing,
  ExtractedDrawingKind,
  ExtractedDrawingStoreyKind,
} from '../models/project-file-drawing.model';
import type { PlanCoverage } from '../models/storey-level.model';
import {
  formatPlanNumbers,
  labelLooksLikeGroundFloor,
  labelLooksLikeUnderground,
  labelSaysFormOrUseDiffers,
  labelledStoreyKind,
  parsePlanNumbers,
  UNSPECIFIED_ENTRANCE_PLAN_NUMBER,
} from './extracted-storey-key.helpers';
import { assertUnreachable } from './function.helpers';
import {
  identifierPaperTokens,
  scaleWithPagePaper,
} from './project-file-drawing-raster-scale.helpers';
import {
  placeStoreyLevel,
  specialStoreyLevel,
  storeyLevelPosition,
  storeyLevelPositionKey,
} from './storey-level.helpers';

const UNGROUPED_BUILDING_KEY = '__ungrouped__';

const STOREY_SORT_ORDER: Record<ExtractedDrawingStoreyKind, number> = {
  cellar: 0,
  entrance: 1,
  typical: 2,
  top: 3,
  roof: 4,
  courtyard: 5,
  unknown: 6,
};

const FAMILY_SORT_ORDER: Record<ExtractedDrawingKind, number> = {
  plan: 0,
  section: 1,
  elevation: 2,
  other: 3,
};

const FAMILY_LABEL: Record<ExtractedDrawingKind, string> = {
  plan: 'Plan drawings',
  section: 'Sections',
  elevation: 'Elevations',
  other: 'Other drawings',
};

const STOREY_LABEL: Record<ExtractedDrawingStoreyKind, string> = {
  cellar: 'Källarplan',
  entrance: 'Entréplan',
  typical: 'Normalplan',
  top: 'Översta plan',
  roof: 'Takplan',
  courtyard: 'Gårdsplan',
  unknown: 'Unknown level',
};

export type ExtractedDrawingGroup = {
  buildingKey: string;
  buildingLabel: string;
  familyId: ExtractedDrawingKind;
  familyLabel: string;
  storeyKind: ExtractedDrawingStoreyKind | null;
  storeyLabel: string | null;
  drawings: ExtractedDrawing[];
};

const buildingKeyOf = (drawing: ExtractedDrawing): string => {
  const id = drawing.buildingInstanceId?.trim();
  if (id) return id;
  return UNGROUPED_BUILDING_KEY;
};

const buildingLabelOf = (drawing: ExtractedDrawing, key: string): string => {
  if (key === UNGROUPED_BUILDING_KEY) return 'Ungrouped';
  const label = drawing.buildingLabel?.trim();
  if (label) return label;
  return key;
};

const familyOf = (drawing: ExtractedDrawing): ExtractedDrawingKind =>
  drawing.drawingKind;

const storeyCaption = (drawing: ExtractedDrawing): string | null => {
  if (drawing.drawingKind !== 'plan') return null;
  if (drawing.storeyKind === 'typical' && drawing.planNumbers.length > 0) {
    return formatPlanNumbers(drawing.planNumbers);
  }
  return STOREY_LABEL[drawing.storeyKind];
};

const firstPlanNumber = (drawing: ExtractedDrawing): number =>
  drawing.planNumbers[0] ?? Number.POSITIVE_INFINITY;

const compareDrawings = (a: ExtractedDrawing, b: ExtractedDrawing): number => {
  const storey =
    STOREY_SORT_ORDER[a.storeyKind] - STOREY_SORT_ORDER[b.storeyKind];
  if (storey !== 0) return storey;
  const indexA = firstPlanNumber(a);
  const indexB = firstPlanNumber(b);
  if (indexA !== indexB) return indexA - indexB;
  if (a.pageIndex !== b.pageIndex) return a.pageIndex - b.pageIndex;
  return a.id.localeCompare(b.id);
};

const compareBuildingKeys = (a: string, b: string): number => {
  if (a === UNGROUPED_BUILDING_KEY) return 1;
  if (b === UNGROUPED_BUILDING_KEY) return -1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Group cropped drawings: building → view family → storey (plans only).
 * Empty groups are omitted. Ungrouped buildings sort last.
 */
export const groupExtractedDrawings = (
  drawings: readonly ExtractedDrawing[],
): ExtractedDrawingGroup[] => {
  const buckets = new Map<string, ExtractedDrawing[]>();
  const labels = new Map<string, string>();

  for (const drawing of drawings) {
    const buildingKey = buildingKeyOf(drawing);
    const family = familyOf(drawing);
    const storeyKey =
      drawing.drawingKind === 'plan' ? drawing.storeyKind : 'none';
    const bucketKey = `${buildingKey}\0${family}\0${storeyKey}`;
    const list = buckets.get(bucketKey);
    if (list) list.push(drawing);
    else buckets.set(bucketKey, [drawing]);
    if (!labels.has(buildingKey)) {
      labels.set(buildingKey, buildingLabelOf(drawing, buildingKey));
    }
  }

  const groups: ExtractedDrawingGroup[] = [];
  for (const [bucketKey, items] of buckets) {
    const [buildingKey, familyId] = bucketKey.split('\0') as [
      string,
      ExtractedDrawingKind,
      string,
    ];
    const first = items[0];
    if (!first) continue;
    const isPlan = familyId === 'plan';
    groups.push({
      buildingKey,
      buildingLabel: labels.get(buildingKey) ?? buildingKey,
      familyId,
      familyLabel: FAMILY_LABEL[familyId],
      storeyKind: isPlan ? first.storeyKind : null,
      storeyLabel: isPlan ? storeyCaption(first) : null,
      drawings: [...items].sort(compareDrawings),
    });
  }

  groups.sort((a, b) => {
    const building = compareBuildingKeys(a.buildingKey, b.buildingKey);
    if (building !== 0) return building;
    const family =
      FAMILY_SORT_ORDER[a.familyId] - FAMILY_SORT_ORDER[b.familyId];
    if (family !== 0) return family;
    const storeyA = a.storeyKind ? STOREY_SORT_ORDER[a.storeyKind] : 0;
    const storeyB = b.storeyKind ? STOREY_SORT_ORDER[b.storeyKind] : 0;
    return storeyA - storeyB;
  });

  return groups;
};

export const extractedDrawingKindLabel = (kind: ExtractedDrawingKind): string =>
  FAMILY_LABEL[kind];

export const extractedDrawingStoreyLabel = (
  kind: ExtractedDrawingStoreyKind,
): string => STOREY_LABEL[kind];

/** Same printed plan numbers, else the same placement; otherwise the crop's own id. */
const planStoreyKey = (drawing: ExtractedDrawing): string => {
  const building = drawing.buildingInstanceId?.trim() || '_';
  if (drawing.planNumbers.length > 0) {
    return `${building}\0p:${drawing.planNumbers.join(',')}`;
  }
  const placement = planCoverageKey(drawing.coverage);
  if (placement !== null) return `${building}\0${placement}`;
  return `${building}\0${drawing.id}`;
};

const planCoverageKey = (coverage: PlanCoverage | null): string | null => {
  if (coverage === null) return null;
  switch (coverage.kind) {
    case 'remaining':
      return 'remaining';
    case 'specific':
      return storeyLevelPositionKey(storeyLevelPosition(coverage.level));
    default:
      return assertUnreachable(coverage);
  }
};

/**
 * A repeated plan: the typical/normalplan, or one sheet that covers several
 * storeys above the entrance. Printed numbers name those storeys. They do
 * not make the sheet specific. One printed plan number is one storey.
 * A label that says the form or use differs is not reused.
 */
const isRepeatedPlan = (
  drawing: Pick<
    ExtractedDrawing,
    'drawingKind' | 'storeyKind' | 'planNumbers' | 'viewLabel'
  >,
): boolean => {
  if (drawing.drawingKind !== 'plan') return false;
  if (labelSaysFormOrUseDiffers(drawing.viewLabel)) return false;
  if (
    drawing.storeyKind === 'cellar' ||
    drawing.storeyKind === 'entrance' ||
    drawing.storeyKind === 'roof' ||
    drawing.storeyKind === 'courtyard' ||
    labelLooksLikeUnderground(drawing.viewLabel) ||
    labelLooksLikeGroundFloor(drawing.viewLabel)
  ) {
    return false;
  }
  const labelled = labelledStoreyKind(drawing.viewLabel);
  if (
    labelled === 'roof' ||
    labelled === 'attic' ||
    labelled === 'technical' ||
    labelled === 'mezzanine' ||
    labelled === 'split_level'
  ) {
    return false;
  }
  if (drawing.planNumbers.length === 1) return false;
  if (drawing.storeyKind === 'typical' || labelled === 'typical') return true;
  return drawing.planNumbers.length >= 2;
};

/**
 * Coverage one crop implies on its own: a takplan, a named attic / entresol /
 * suterräng, a garage or cellar under the entrance, the entrance plan, or a
 * repeated plan. A sheet that shows one storey stays unplaced until
 * {@link placeDrawingCoverages} knows the entrance plan.
 */
export const drawingPlanCoverage = (
  drawing: Pick<
    ExtractedDrawing,
    'drawingKind' | 'storeyKind' | 'planNumbers' | 'viewLabel'
  >,
): PlanCoverage | null => {
  if (drawing.drawingKind !== 'plan') return null;
  const labelled = labelledStoreyKind(drawing.viewLabel);
  if (drawing.storeyKind === 'roof' || labelled === 'roof') {
    return { kind: 'specific', level: { kind: 'roof' } };
  }
  if (
    labelled === 'attic' ||
    labelled === 'technical' ||
    labelled === 'mezzanine' ||
    labelled === 'split_level'
  ) {
    return { kind: 'specific', level: specialStoreyLevel(labelled) };
  }
  if (
    drawing.planNumbers.length === 0 &&
    (drawing.storeyKind === 'cellar' ||
      labelLooksLikeUnderground(drawing.viewLabel))
  ) {
    return { kind: 'specific', level: { kind: 'single', index: -1 } };
  }
  if (drawing.storeyKind === 'entrance' && drawing.planNumbers.length <= 1) {
    return { kind: 'specific', level: { kind: 'single', index: 0 } };
  }
  if (isRepeatedPlan(drawing)) return { kind: 'remaining' };
  return null;
};

const withPlacedLevel = (
  coverage: PlanCoverage | null,
  planNumbers: readonly number[],
  entrancePlanNumber: number,
): PlanCoverage | null => {
  const current = coverage?.kind === 'specific' ? coverage.level : null;
  if (coverage?.kind === 'remaining') return coverage;
  const level = placeStoreyLevel({
    level: current,
    planNumbers,
    entrancePlanNumber,
  });
  return level === null || level.kind === 'typical'
    ? coverage
    : { kind: 'specific', level };
};

const buildingKeyOfDrawing = (
  drawing: Pick<ExtractedDrawing, 'buildingInstanceId'>,
): string => drawing.buildingInstanceId?.trim() || '_';

/**
 * A takplan stays a roof plan. A printed plan number on that sheet is the
 * storey it shows; it does not turn the sheet into that numeric index.
 */
const isRoofSheet = (
  drawing: Pick<ExtractedDrawing, 'drawingKind' | 'storeyKind' | 'viewLabel'>,
): boolean =>
  drawing.drawingKind === 'plan' &&
  (drawing.storeyKind === 'roof' ||
    labelledStoreyKind(drawing.viewLabel) === 'roof');

const repeatedSheetTokens = (
  drawings: readonly ExtractedDrawing[],
): string[] => {
  if (drawings.length < 2) return [];
  const tokenSets = drawings.map((drawing) =>
    identifierPaperTokens(drawing.drawingId),
  );
  const [first, ...rest] = tokenSets;
  if (first == null) return [];
  return first.filter((token) => rest.every((set) => set.includes(token)));
};

const withPagePaper = (
  drawing: ExtractedDrawing,
  options: {
    fileName: string | null;
    repeatedSheetTokens: readonly string[];
  },
): ExtractedDrawing => {
  const onThisSheet = identifierPaperTokens(drawing.drawingId);
  const repeated = options.repeatedSheetTokens.filter((token) =>
    onThisSheet.includes(token),
  );
  const reading = scaleWithPagePaper(
    drawing.scale,
    {
      widthMm: drawing.pageWidthMm ?? null,
      heightMm: drawing.pageHeightMm ?? null,
    },
    {
      fileName: options.fileName,
      repeatedSheetTokens: repeated,
    },
  );
  return { ...drawing, ...reading };
};

/**
 * Plan numbers printed on the sheet. An empty list keeps numbers the detector
 * already stored. A label that prints none stays empty. The entrance number
 * is not inferred from the next numbered sheet.
 */
const printedPlanNumbers = (
  drawing: Pick<ExtractedDrawing, 'planNumbers' | 'viewLabel'>,
): number[] => {
  const fromLabel = parsePlanNumbers(drawing.viewLabel);
  return fromLabel.length > 0 ? fromLabel : [...drawing.planNumbers];
};

/**
 * Where each building's entrance sits in the printed plan numbers.
 * A number the model or the sheet already gave wins. Otherwise the gap
 * fill is {@link UNSPECIFIED_ENTRANCE_PLAN_NUMBER}. `_` is a drawing with
 * no building id.
 */
export type PlaceDrawingCoveragesOptions = {
  entrancePlanByBuilding?: ReadonlyMap<string, number>;
  /** File name per drawing. Omitted names add no file-name claim. */
  fileNameByDrawingId?: ReadonlyMap<string, string>;
};

/**
 * Plan number already on the sheet. Does not invent one.
 * A printed label wins over a stored list only when the label prints numbers.
 */
const statedPlanNumber = (
  drawing: Pick<ExtractedDrawing, 'planNumbers' | 'viewLabel'>,
): number | undefined => {
  const printed = parsePlanNumbers(drawing.viewLabel);
  if (printed.length === 1) return printed[0];
  if (drawing.planNumbers.length === 1) return drawing.planNumbers[0];
  return undefined;
};

/**
 * Batch step: per building, the entrance plan number places every sheet
 * that shows one storey. A repeated plan stays `remaining` even when its
 * title prints the plan numbers it stands for. Those numbers are kept.
 * An entrance the model or the sheet already numbered keeps that number.
 * An entrance with no number takes the stated entrance, or Plan 10.
 * Storey kind is left as the model gave it.
 */
export const placeDrawingCoverages = (
  drawings: readonly ExtractedDrawing[],
  options?: PlaceDrawingCoveragesOptions,
): ExtractedDrawing[] => {
  const prepared = drawings.map((drawing) => {
    if (drawing.drawingKind !== 'plan') return drawing;
    const planNumbers = printedPlanNumbers(drawing);
    const numbersChanged =
      planNumbers.join(',') !== drawing.planNumbers.join(',');
    const next = numbersChanged ? { ...drawing, planNumbers } : drawing;
    if (isRepeatedPlan(next)) {
      const remaining: PlanCoverage = { kind: 'remaining' };
      return next.coverage?.kind === 'remaining'
        ? next
        : { ...next, coverage: remaining };
    }
    const underground =
      planNumbers.length === 0 && labelLooksLikeUnderground(next.viewLabel);
    const staleNormalplan =
      next.coverage?.kind === 'remaining' &&
      (planNumbers.length > 0 || underground);
    if (!numbersChanged && !staleNormalplan && !underground) return drawing;
    if (!staleNormalplan && !underground) return next;
    return {
      ...next,
      coverage: drawingPlanCoverage(next),
    };
  });
  const withEntrance = prepared.map((drawing) => {
    if (drawing.drawingKind !== 'plan' || drawing.storeyKind !== 'entrance') {
      return drawing;
    }
    if (statedPlanNumber(drawing) !== undefined) return drawing;
    const building = buildingKeyOfDrawing(drawing);
    const stated = options?.entrancePlanByBuilding?.get(building);
    return {
      ...drawing,
      planNumbers: [stated ?? UNSPECIFIED_ENTRANCE_PLAN_NUMBER],
    };
  });
  const entrancePlans = new Map<string, Set<number>>();
  for (const drawing of withEntrance) {
    if (drawing.drawingKind !== 'plan' || drawing.storeyKind !== 'entrance') {
      continue;
    }
    const only =
      drawing.planNumbers.length === 1 ? drawing.planNumbers[0] : undefined;
    if (only === undefined) continue;
    const building = buildingKeyOfDrawing(drawing);
    const set = entrancePlans.get(building) ?? new Set<number>();
    set.add(only);
    entrancePlans.set(building, set);
  }
  const placed = withEntrance.map((drawing) => {
    if (isRoofSheet(drawing)) {
      const roof: PlanCoverage = { kind: 'specific', level: { kind: 'roof' } };
      if (
        drawing.coverage?.kind === 'specific' &&
        drawing.coverage.level.kind === 'roof'
      ) {
        return drawing;
      }
      return { ...drawing, coverage: roof };
    }
    if (isRepeatedPlan(drawing)) return drawing;
    const building = buildingKeyOfDrawing(drawing);
    const plans = entrancePlans.get(building);
    const fromSheet = plans?.size === 1 ? [...plans][0] : undefined;
    const entrance =
      fromSheet ?? options?.entrancePlanByBuilding?.get(building);
    if (entrance === undefined || drawing.planNumbers.length === 0) {
      return drawing;
    }
    const coverage = drawing.coverage;
    const level = withPlacedLevel(coverage, drawing.planNumbers, entrance);
    if (level === drawing.coverage && coverage === drawing.coverage) {
      return drawing;
    }
    return { ...drawing, coverage: level };
  });
  const repeated = repeatedSheetTokens(drawings);
  return placed.map((drawing) =>
    withPagePaper(drawing, {
      fileName: options?.fileNameByDrawingId?.get(drawing.id) ?? null,
      repeatedSheetTokens: repeated,
    }),
  );
};

const rasterArea = (drawing: ExtractedDrawing): number | null => {
  if (drawing.pixelWidth == null || drawing.pixelHeight == null) return null;
  return drawing.pixelWidth * drawing.pixelHeight;
};

const betterPlanCrop = (
  current: ExtractedDrawing,
  next: ExtractedDrawing,
): ExtractedDrawing => {
  const currentArea = rasterArea(current);
  const nextArea = rasterArea(next);
  if (currentArea != null && nextArea != null && currentArea !== nextArea) {
    return currentArea > nextArea ? current : next;
  }
  if (currentArea != null && nextArea == null) return current;
  if (nextArea != null && currentArea == null) return next;
  const currentBbox = current.bbox.width * current.bbox.height;
  const nextBbox = next.bbox.width * next.bbox.height;
  if (currentBbox !== nextBbox) return currentBbox > nextBbox ? current : next;
  return current.id < next.id ? current : next;
};

/**
 * One plan crop per building storey, for a stack of floor sheets.
 * Crops that share a storey index collapse to the largest raster.
 * A crop with no storey index stays on its own.
 */
export const pickBestPlanPerStorey = (
  drawings: readonly ExtractedDrawing[],
): ExtractedDrawing[] => {
  const winners = new Map<string, ExtractedDrawing>();
  for (const drawing of drawings) {
    const key = planStoreyKey(drawing);
    const current = winners.get(key);
    if (current == null) {
      winners.set(key, drawing);
      continue;
    }
    winners.set(key, betterPlanCrop(current, drawing));
  }
  return [...winners.values()];
};
